import { INTERNAL_CLIENT_ID } from "@/constants/CProjects";

import { TProjectClient, TProjectSummary, TProjectsFilters } from "@/types";

/** One client's projects on the /projects page; `client` null = the internal ones. */
export type TProjectGroup = {
	/** The client's id, or INTERNAL_CLIENT_ID: the accordion item's value. */
	id: number;
	client: TProjectClient | null;
	projects: TProjectSummary[];
	/** The group's worked hours over the period, in decimal hours. */
	workedHours: number;
};

/** Lower case, accents dropped: "Élan" finds "elan" and the other way round. */
const fold = (value: string) =>
	value
		.normalize("NFD")
		.replace(/\p{Diacritic}/gu, "")
		.toLowerCase();

/** The id the client filter matches a project on. */
export const clientIdOf = (project: Pick<TProjectSummary, "client">): number =>
	project.client?.id ?? INTERNAL_CLIENT_ID;

/**
 * What the /projects filters keep: archived projects only when asked,
 * the chosen clients (none chosen = all), and a search on the project's
 * or its client's name.
 */
export const filterProjects = (
	projects: TProjectSummary[],
	{
		search,
		clientIds,
		archived,
	}: Pick<TProjectsFilters, "search" | "clientIds" | "archived">
): TProjectSummary[] => {
	const needle = fold((search ?? "").trim());
	// ActiveFilters clears a list's last chip to null, not [].
	const clients = new Set(clientIds ?? []);

	return projects.filter(
		(project) =>
			(archived || !project.isArchived) &&
			(0 === clients.size || clients.has(clientIdOf(project))) &&
			("" === needle ||
				fold(project.name).includes(needle) ||
				fold(project.client?.name ?? "").includes(needle))
	);
};

/**
 * The projects by client: clients by name, the internal group last,
 * projects by name within a group, each group's hours summed.
 */
export const groupProjectsByClient = (
	projects: TProjectSummary[]
): TProjectGroup[] => {
	const groups = new Map<number, TProjectGroup>();

	for (const project of projects) {
		const id = clientIdOf(project);
		const group = groups.get(id) ?? {
			id,
			client: project.client,
			projects: [],
			workedHours: 0,
		};
		group.projects.push(project);
		group.workedHours += project.workedHours.raw;
		groups.set(id, group);
	}

	const byName = (a: string, b: string) => a.localeCompare(b);

	return [...groups.values()]
		.map((group) => ({
			...group,
			projects: [...group.projects].sort((a, b) =>
				byName(a.name, b.name)
			),
		}))
		.sort((a, b) => {
			if (!a.client) return 1;
			if (!b.client) return -1;
			return byName(a.client.name, b.client.name);
		});
};
